import {
    Features,
    useFeatureFlagValue,
} from '@experiences/feature-flags';
import { useApolloTheme } from '@experiences/theme';
import {
    Box,
    Checkbox,
    FormControlLabel,
    RadioGroup,
    Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import Tokens from '@uipath/apollo-core';
import {
    type ThemeType,
    defaultTheme,
    getApolloThemeFromTheme,
    setTheme,
} from '@uipath/portal-shell-util';
import clsx from 'clsx';
import type { FC } from 'react';
import React, {
    useCallback,
    useEffect,
    useRef,
} from 'react';
import {
    Controller,
    useForm,
} from 'react-hook-form';
import { useIntl } from 'react-intl';

import useSetUserTheme from '../../../common/hooks/useSetUserTheme.default';
import { useTelemetryHelper } from '../../../telemetry/TelemetryHelper';
import darkThemeThumbnail from './assets/darkThemeThumbnail.svg';
import highContrastThemeThumbnail from './assets/highContrastThemeThumbnail.svg';
import lightThemeThumbnail from './assets/lightThemeThumbnail.svg';
import ThemeRadioComponent from './ThemeRadioComponent';

const useStyles = makeStyles(theme =>
    createStyles({
        formContainer: { maxWidth: '482px' },
        heading: {
            color: theme.palette.semantic.colorForeground,
            fontSize: Tokens.FontFamily.FontLSize,
            fontWeight: Tokens.FontFamily.FontWeightSemibold,
        },
        mainHeading: { paddingBottom: Tokens.Padding.PadXxl },
        defaultThemesWrapper: {
            display: 'flex',
            gap: Tokens.Padding.PadXxxl,
        },
        systemSettingsDescription: { marginLeft: Tokens.Spacing.SpacingXl },
        systemSyncControlLabel: { fontWeight: Tokens.FontFamily.FontWeightSemibold },
        radioGroup: { marginLeft: Tokens.Spacing.SpacingXl },
        section: { paddingBottom: Tokens.Spacing.SpacingM },
        subHeading: {
            color: theme.palette.semantic.colorForeground,
            fontSize: Tokens.FontFamily.FontMSize,
            fontWeight: Tokens.FontFamily.FontWeightSemibold,
        },
    }),
);

interface IThemePreferencesData {
    theme: ThemeType;
}

const ProfileThemeSettingsComponent: FC = () => {
    const { formatMessage: translate } = useIntl();
    const classes = useStyles();
    const { selectedThemeId } = useApolloTheme();
    const {
        control, setValue,
    } = useForm<IThemePreferencesData>({
        mode: 'onChange',
        defaultValues: { theme: selectedThemeId ?? defaultTheme },
    });

    useEffect(() => {
        if (selectedThemeId) {
            setValue('theme', selectedThemeId);
        }
    }, [ selectedThemeId, setValue ]);

    const setUserTheme = useSetUserTheme();
    const prevThemeRef = useRef<ThemeType>();
    const { logEvent } = useTelemetryHelper();

    const EnableUserTheme = useFeatureFlagValue(Features.EnableUserTheme.name);
    const highContrastLightThemeFlag = useFeatureFlagValue(Features.EnableHighContrastLightTheme.name);

    const handleThemeSelect = useCallback((newTheme: ThemeType) => {
        if (!highContrastLightThemeFlag && newTheme === 'light-hc') {
            return;
        }

        logEvent('Theme.ChangeTheme', {
            ProductThemeOld: prevThemeRef.current,
            ProductThemeNew: newTheme,
        });
        if (EnableUserTheme) {
            setUserTheme(newTheme);
        } else {
            setTheme(newTheme);
        }
        prevThemeRef.current = newTheme;
    }, [ EnableUserTheme, highContrastLightThemeFlag, logEvent, setUserTheme ]);

    return (
        <Controller
            control={control}
            name="theme"
            rules={{ required: true }}
            render={({
                onChange, value,
            }) => {
                const disableRadios = value === 'autoTheme';
                const theme = disableRadios ? getApolloThemeFromTheme(value) : value;

                return (
                    <Box>
                        <Typography
                            className={clsx(classes.heading, classes.mainHeading)}
                            role="heading"
                            aria-level={1}>
                            {translate({ id: 'CLIENT_THEME' })}
                        </Typography>
                        <Typography>
                            {translate({ id: 'CLIENT_THEME_SETTINGS_DESCRIPTION' })}
                        </Typography>
                        <FormControlLabel
                            control={<Checkbox
                                checked={value === 'autoTheme'}
                                color="primary"
                                data-cy="sync-with-system-checkbox"
                                onChange={event => {
                                    const themeToSet = event.target.checked ? 'autoTheme' : theme;

                                    onChange(themeToSet);
                                    handleThemeSelect(themeToSet as ThemeType);
                                }}
                            />}
                            label={<Typography className={classes.systemSyncControlLabel}>
                                { translate({ id: 'CLIENT_SYNC_WITH_SYSTEM' })}
                            </Typography>}
                        />
                        <Typography className={classes.systemSettingsDescription}>
                            {translate({ id: 'CLIENT_SYNC_WITH_SYSTEM_DESCRIPTION' })}
                        </Typography>
                        <br />

                        <RadioGroup
                            data-cy="profile-preferences-theme-radio-buttons"
                            aria-label={translate({ id: 'CLIENT_THEME_CONTROL_DESCRIPTION' })}
                            className={classes.radioGroup}
                            value={value}
                            onChange={event => {
                                onChange(event.target.value);
                                handleThemeSelect(event.target.value as ThemeType);
                            }}
                        >
                            <Box className={classes.defaultThemesWrapper}>
                                <ThemeRadioComponent
                                    theme="light"
                                    value={theme}
                                    disabled={disableRadios}
                                    imgSrc={lightThemeThumbnail}
                                    label={translate({ id: 'CLIENT_LIGHT_THEME' })} />
                                <ThemeRadioComponent
                                    theme="dark"
                                    value={theme}
                                    disabled={disableRadios}
                                    imgSrc={darkThemeThumbnail}
                                    label={translate({ id: 'CLIENT_DARK_THEME' })} />
                            </Box>
                            <br />
                            <ThemeRadioComponent
                                theme="light-hc"
                                value={theme}
                                disabled={disableRadios}
                                imgSrc={highContrastThemeThumbnail}
                                label={translate({ id: 'CLIENT_LIGHT_HIGH_CONTRAST_THEME' })} />
                        </RadioGroup>
                    </Box>
                );
            }}
        />

    );
};

export default ProfileThemeSettingsComponent;
